/**
 * store-utils.js — native replacements for three small Vitals modules.
 *
 *   1. Favicon Cart Count    — badges the cart item count onto the favicon
 *   2. Inactive Tab Message  — swaps the tab title when the visitor leaves
 *   3. Content Protection    — blocks right-click / copy / drag
 *
 * Each is independent and gated on a theme setting, so any of them can be
 * turned off in Theme settings → Store utilities without touching code.
 * Config is injected by layout/theme.liquid as window.StoreUtilsConfig.
 */
(function () {
  'use strict';

  var cfg = window.StoreUtilsConfig || {};

  /* ------------------------------------------------------------------ *
   * 1. Favicon cart count
   * ------------------------------------------------------------------ */
  var Favicon = {
    original: null,
    baseImage: null,

    init: function () {
      var link = document.querySelector('link[rel~="icon"]');
      if (!link || !link.href) return;

      this.original = link.href;

      var img = new Image();
      img.crossOrigin = 'anonymous'; // Shopify CDN sends permissive CORS
      img.onload = function () {
        Favicon.baseImage = img;
        Favicon.refresh();
      }.bind(this);
      img.onerror = function () {
        // Tainted or unreachable icon — skip silently rather than break.
        Favicon.baseImage = null;
      };
      img.src = this.original;

      // Update whenever the theme touches the cart.
      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach(function (evt) {
        document.addEventListener(evt, function () {
          setTimeout(Favicon.refresh.bind(Favicon), 120);
        });
      });
    },

    refresh: function () {
      if (!this.baseImage) return;
      fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (cart) Favicon.draw(cart.item_count || 0);
        })
        .catch(function () { /* offline — leave the icon alone */ });
    },

    draw: function (count) {
      var size = 64;
      var canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      var ctx = canvas.getContext('2d');

      try {
        ctx.drawImage(this.baseImage, 0, 0, size, size);
      } catch (e) {
        return; // canvas tainted — cannot export, so do nothing
      }

      if (count > 0) {
        var r = size * 0.32;
        var cx = size - r - 2;
        var cy = r + 2;

        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = cfg.faviconBadgeColor || '#d32f2f';
        ctx.fill();

        ctx.fillStyle = cfg.faviconTextColor || '#ffffff';
        ctx.font = 'bold ' + Math.round(r * 1.25) + 'px -apple-system, Helvetica, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(count > 99 ? '99+' : String(count), cx, cy + 1);
      }

      var url;
      try {
        url = canvas.toDataURL('image/png');
      } catch (e) {
        return;
      }
      this.apply(count > 0 ? url : this.original);
    },

    apply: function (href) {
      var links = document.querySelectorAll('link[rel~="icon"]');
      if (!links.length) return;
      Array.prototype.forEach.call(links, function (l) {
        l.href = href;
      });
    },
  };

  /* ------------------------------------------------------------------ *
   * 2. Inactive tab message
   * ------------------------------------------------------------------ */
  var InactiveTab = {
    realTitle: null,

    init: function () {
      if (!cfg.inactiveTabText) return;
      this.realTitle = document.title;

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          InactiveTab.realTitle = InactiveTab.realTitle || document.title;
          document.title = cfg.inactiveTabText;
        } else if (InactiveTab.realTitle) {
          document.title = InactiveTab.realTitle;
        }
      });
    },
  };

  /* ------------------------------------------------------------------ *
   * 3. Content protection
   *
   * NOTE: this is a deterrent, not security. Anyone can still read the
   * page source, and it does inconvenience legitimate visitors. Kept
   * because it replaces a Vitals module that was switched on.
   * ------------------------------------------------------------------ */
  var ContentProtection = {
    init: function () {
      var block = function (e) {
        // Never interfere with form fields — that would break checkout,
        // search, and the quantity inputs.
        var t = e.target;
        if (t && t.closest && t.closest('input, textarea, select, [contenteditable="true"]')) return;
        e.preventDefault();
        return false;
      };

      ['contextmenu', 'selectstart', 'copy', 'cut', 'dragstart'].forEach(function (evt) {
        document.addEventListener(evt, block);
      });

      document.addEventListener('keydown', function (e) {
        var t = e.target;
        if (t && t.closest && t.closest('input, textarea, select, [contenteditable="true"]')) return;
        var k = (e.key || '').toLowerCase();
        if ((e.ctrlKey || e.metaKey) && ['u', 's', 'c', 'x', 'a'].indexOf(k) !== -1) {
          e.preventDefault();
          return false;
        }
      });
    },
  };

  function boot() {
    if (cfg.faviconCartCount) Favicon.init();
    if (cfg.inactiveTabEnabled) InactiveTab.init();
    if (cfg.contentProtection) ContentProtection.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/**
 * Cart-drawer volume upsell button.
 *
 * Rather than calling /cart/change.js ourselves, we set the value of the
 * theme's own quantity input for that line and dispatch `change`. The theme's
 * existing handler then does the update, the loading state, the error handling
 * and the section re-render — so this stays correct if Clean Canvas changes
 * how the cart works.
 *
 * Delegated from document because the drawer re-renders its own markup.
 */
document.addEventListener('click', function (e) {
  var btn = e.target && e.target.closest && e.target.closest('.js-vt-bump');
  if (!btn) return;

  var input = document.getElementById(btn.dataset.input);
  if (!input) return;

  var target = parseInt(btn.dataset.targetQty, 10);
  if (Number.isNaN(target) || target < 1) return;

  // Respect stock limits the theme has already applied to the input.
  var max = parseInt(input.getAttribute('max'), 10);
  if (!Number.isNaN(max) && target > max) target = max;

  if (String(target) === String(input.value)) return;

  btn.setAttribute('disabled', 'disabled');
  input.value = target;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});
